'use client';

import { useMemo, useState } from 'react';
import { MetaDefault } from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  CheckmarkCircle01Icon,
  CheckListIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
  type TodoItem,
  type TodosMessage,
} from '@terra/shared-dto-chat-types';
import { useCopilotPlan } from '../../hooks/useCopilotPlan';

function extractLatestTodos(messages: TerraUIMessage[]): TodoItem[] {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = messages[messageIndex];

    if (message.role !== 'assistant' || !message.parts) {
      continue;
    }

    for (
      let partIndex = message.parts.length - 1;
      partIndex >= 0;
      partIndex--
    ) {
      const part = message.parts[partIndex];

      if (
        part.type === TerraUIMessageDataPartName &&
        (
          part.data as {
            type?: string;
          }
        ).type === DataMessageTypeEnum.Todos
      ) {
        return (part.data as TodosMessage).todos ?? [];
      }
    }
  }

  return [];
}

export function CopilotPinnedPlanHeader({
  chatId,
  messages,
}: {
  chatId: string;
  messages: TerraUIMessage[];
}) {
  const t = useTranslations('chat.copilot.plan');
  const [isExpanded, setIsExpanded] = useState(false);
  const { latestPlan, openPlanTab } = useCopilotPlan({ chatId, messages });
  const todos = useMemo(() => extractLatestTodos(messages), [messages]);

  if (!latestPlan) {
    return null;
  }

  const totalCount = todos.length;
  const completedCount = todos.filter(
    (todo) => todo.state === 'completed',
  ).length;
  const allDone = totalCount > 0 && completedCount === totalCount;

  return (
    <VStack
      alignItems="stretch"
      gap={0}
      w="full"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
    >
      <HStack gap={2} justifyContent="space-between" w="full" px={3} py={1.5}>
        <HStack
          gap={2}
          minW={0}
          cursor="pointer"
          onClick={openPlanTab}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              openPlanTab();
            }
          }}
        >
          <Box color="fg.muted" display="flex" flexShrink={0}>
            <CheckListIcon size={13} />
          </Box>
          <MetaDefault fontWeight="medium" color="fg.muted" flexShrink={0}>
            {t('buildLabel')}
          </MetaDefault>
          <MetaDefault color="fg.default" truncate>
            {latestPlan.title || t('tabTitle')}
          </MetaDefault>
        </HStack>
      </HStack>

      {totalCount > 0 ? (
        <>
          <HStack
            gap={1.5}
            w="full"
            px={3}
            py={1.5}
            cursor="pointer"
            borderTopWidth="1px"
            borderTopColor="border.subtle"
            role="button"
            tabIndex={0}
            onClick={() => setIsExpanded((previous) => !previous)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setIsExpanded((previous) => !previous);
              }
            }}
          >
            <Box
              color={allDone ? 'fg.muted' : 'fg.subtle'}
              display="flex"
              flexShrink={0}
            >
              <CheckmarkCircle01Icon size={13} />
            </Box>
            <MetaDefault color="fg.muted" flexShrink={0}>
              {t('todosCompleted', {
                completed: completedCount,
                total: totalCount,
              })}
            </MetaDefault>
            <Box color="fg.subtle" display="flex" flexShrink={0} ml="auto">
              {isExpanded ? (
                <ArrowDown01Icon size={13} />
              ) : (
                <ArrowRight01Icon size={13} />
              )}
            </Box>
          </HStack>

          {isExpanded ? (
            <VStack
              alignItems="stretch"
              gap={1}
              px={3}
              pb={2}
              pt={0.5}
              borderTopWidth="1px"
              borderTopColor="border.subtle"
              maxHeight="220px"
              overflowY="auto"
            >
              {todos.map((todo) => {
                const isDone = todo.state === 'completed';

                return (
                  <HStack key={todo.id} gap={2} alignItems="flex-start">
                    <Box
                      color={isDone ? 'fg.muted' : 'fg.subtle'}
                      display="flex"
                      flexShrink={0}
                      mt="2px"
                    >
                      {isDone ? (
                        <CheckmarkCircle01Icon size={12} />
                      ) : (
                        <Box
                          w="12px"
                          h="12px"
                          borderWidth="1px"
                          borderColor="border.default"
                          borderRadius="full"
                        />
                      )}
                    </Box>
                    <MetaDefault
                      color={isDone ? 'fg.subtle' : 'fg.default'}
                      textDecoration={isDone ? 'line-through' : undefined}
                    >
                      {todo.content}
                    </MetaDefault>
                  </HStack>
                );
              })}
            </VStack>
          ) : null}
        </>
      ) : null}
    </VStack>
  );
}
